import React, { useState } from "react";
import { FaX, FaUpload, FaTrash, FaAngleDown } from "react-icons/fa6";
import { GoUpload } from "react-icons/go";
import Button from "../../../../components/button-v1/index";
import CustomInput from "../../../../components/ui/input";
import { Dropdown } from "../../../../components/ui/dropdown";
import { useUploadVideo, useSyncVideoMetadata } from "../../../../utils/youtubeApiQueries";
import toast from "react-hot-toast";
import styles from "./uploadVideoModal.module.css";

const parseDuration = (pt) => {
  if (!pt) return "0:00";
  // If it's already in mm:ss / h:mm:ss format, keep it.
  if (typeof pt === "string" && pt.includes(":")) return pt;
  const hours = pt.match(/(\d+)H/);
  const minutes = pt.match(/(\d+)M/);
  const seconds = pt.match(/(\d+)S/);
  const h = hours ? parseInt(hours[1]) : 0;
  const m = minutes ? parseInt(minutes[1]) : 0;
  const s = seconds ? parseInt(seconds[1]) : 0;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const UploadModal = ({ open, onClose, categories = [], onAddVideo, isSignedIn, requestSignIn, onYouTubeChanged }) => {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const uploadVideoMutation = useUploadVideo();
  const syncVideoMetadataMutation = useSyncVideoMetadata();

  const categoryOptions = categories.filter(cat => cat.value !== "all");

  const selectedCategoryLabel =
    category
      ? categoryOptions.find(cat => cat.value === category)?.label || category
      : "Select category";

  async function waitForYoutubeProcessing({ videoUuid, timeoutMs = 120000, intervalMs = 4000 }) {
    const started = Date.now();
    // Keep polling metadata until duration is present or timeout.
    while (Date.now() - started < timeoutMs) {
      try {
        const updated = await syncVideoMetadataMutation.mutateAsync({ videoId: videoUuid });
        // Once YouTube finished processing, duration/thumbnails usually appear.
        if (updated?.duration || updated?.thumbnail_url) return updated;
      } catch (e) {
        // YouTube may not have metadata ready yet; keep polling.
      }
      await sleep(intervalMs);
    }
    return null;
  }

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const handleUpload = async () => {
    if (!title || !category || !description || !file) {
      toast.error("Please fill in all fields and select a video file");
      return;
    }
    if (!isSignedIn) {
      toast.warning("Please sign in with Google first");
      requestSignIn();
      return;
    }

    if (file) {
      try {
        const metadata = {
          snippet: {
            title,
            description,
            tags: [category],
            categoryId: category,
          },
          status: {
            privacyStatus: "unlisted",
            madeForKids: false,
          },
        };

        const uploaded = await uploadVideoMutation.mutateAsync({
          file,
          metadata,
        });

        onYouTubeChanged?.();

        if (uploaded?.uuid) {
          await waitForYoutubeProcessing({ videoUuid: uploaded.uuid });
          onYouTubeChanged?.();
        }

        const youtubeId = uploaded.youtube_video_id;
        onAddVideo({
          title,
          description,
          category,
          youtubeId,
          duration: parseDuration(uploaded.duration),
          date: (uploaded.created_at || new Date().toISOString()).split("T")[0],
        });

        toast.success("Video uploaded successfully");
        onClose();
        onYouTubeChanged?.();
      } catch (error) {
        console.error(error);
        const errorMessage = error?.message || error?.details?.error || "Upload failed";
        toast.error(errorMessage);
      }
    }
  };

  const isUploadDisabled = uploadVideoMutation.isPending || !(title && category && description && file);

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Upload New Video</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FaX />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.field}>
            <label className={styles.label}>Video Title</label>
            <CustomInput
              placeholder="Enter video title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Category</label>
            <Dropdown
              options={categoryOptions}
              value={category}
              onChange={setCategory}
              trigger={
                <div className={styles.dropdownTrigger}>
                  <span className={styles.triggerText}>{selectedCategoryLabel}</span>
                  <FaAngleDown className={styles.triggerArrow} />
                </div>
              }
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the video"
              rows={4}
              className={styles.textarea}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Video File</label>
            <div
              className={`${styles.fileDropArea} ${dragActive ? styles.dragActive : ""} ${file ? styles.hasFile : ""}`}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
            >
              {file ? (
                <div className={styles.selectedFile}>
                  <p className={styles.fileName}>{file.name}</p>
                  <FaTrash className={styles.trashIcon} onClick={handleRemoveFile} />
                </div>
              ) : (
                <>
                  <GoUpload className={styles.uploadIcon} />
                  <p className={styles.dropText}>Drag & drop or Click to upload</p>
                  <p className={styles.dropSubText}>(Cloud required for actual uploads)</p>
                </>
              )}
              <input
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                className={styles.fileInput}
              />
            </div>
            <p className={styles.fileNote}>
              Supported formats: MP4, MOV, AVI • Max size: 500MB
            </p>
          </div>

        </div>

        <div className={styles.footer}>
          <Button className={styles.cancelButton} onClick={onClose}>
            Cancel
          </Button>
          <Button
            className={styles.actionButton}
            onClick={handleUpload}
            disabled={isUploadDisabled}
          >
            {uploadVideoMutation.isPending ? "Uploading..." : "Upload Video"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;